// ShortsDetail.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { addBreadcrumb } from '@sentry/browser';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import { Swiper, SwiperSlide } from '../component/Swiper.jsx';

import Redirect from '../container/Redirect.js';
import ShortMessage from '../container/ShortMessage.js';
import ShortAd from '../container/ShortAd.js';
import ShortSidebar from '../component/ShortSidebar.jsx';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { Severity } from '../resource/sentry.js';
import { shorts as shortsDebug } from '../resource/debug.js';
import media from '../style/media.js';

const VIRTUAL_OFFSET = 1;
const PREFETCH_OFFSET = 3; // TODO: remote config
const MIN_DESKTOP_LAYOUT_HEIGHT = 900;
const isServer = typeof window === 'undefined';

const log = shortsDebug.extend('log:component/ShortsDetail');

const ShortsDetail = ({
  deviceHeight = null,
  isOnDesktop = false,
  isNextPageFetching = false,
  isFirstPageFetched = false,
  isFirstPageFetching = false,
  isClipFirstPageFetching = false,
  isClipFirstPageFetched = false,
  isConfigMerged = false,
  messageIdFromUrl = null,
  ctaIdFromUrl = null,
  feedId = null,
  clipFetchedFeedId = null,
  clipLatestFeedId = null,
  category = null,
  clipCategory = null,
  setLastWatchedIndex = () => null,
  replace = () => null,
  fetchFeeds = () => null,
  itemIds = [],
  shortsSelectPath = [],
  nextPage = null,
  clipNextPage = null,
  clipLatestTimestamp = null,
  setFirstShortMessagePlayed = () => null,
  addMessageIdToShortWatchedList = () => null,
  prefetchedMessageAmount = 2,
  mongoObjectIdRegexPattern = '',
}) => {
  const { state } = useLocation();
  const listPath = state?.listPath;
  const swiperRef = useRef(null);
  const timeoutRef = useRef(null);
  // User is from header/footer shorts entry or from a direct url
  const isFromShortsEntry = !listPath;
  const shouldShowBack = !isFromShortsEntry;

  const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
  const [currentIndex, setCurrentIndex] = useState(
    messageIdFromUrl
      ? itemIds.indexOf(messageIdFromUrl)
      : itemIds.indexOf(`cta-${ctaIdFromUrl}`)
  );

  log('Rendering', {
    isFromShortsEntry,
    isConfigMerged,
    messageIdFromUrl,
    ctaIdFromUrl,
    listPath,
    category,
    currentIndex,
    ids: itemIds,
  });

  // Set `currentIndex` when users refresh the page.
  useEffect(() => {
    // The `currentIndex` might be -1 when users refresh the page.
    // We'll need to update `currentIndex` after `itemIds` is not empty.
    if (currentIndex === -1 && itemIds.length > 0) {
      log(
        'Set `currentIndex` when user refresh page on `location.state` category shorts'
      );
      const idFromUrl = messageIdFromUrl || ctaIdFromUrl;
      const mongoObjectIdRegex = new RegExp(mongoObjectIdRegexPattern, 'i');
      const isAd = !mongoObjectIdRegex.test(idFromUrl);
      setCurrentIndex(
        itemIds.indexOf(isAd ? `cta-${ctaIdFromUrl}` : messageIdFromUrl)
      );
    }
  }, [
    ctaIdFromUrl,
    currentIndex,
    itemIds,
    messageIdFromUrl,
    mongoObjectIdRegexPattern,
  ]);

  // Fetch `location.state` category feeds first page data if not fetched
  useEffect(() => {
    if (isFromShortsEntry) return;

    if (
      !itemIds.length &&
      !isFirstPageFetched &&
      !isFirstPageFetching &&
      category
    ) {
      log('Fetch `location.state` category feeds first page data', {
        category,
      });
      fetchFeeds({
        page: 1,
        type: category,
      });
    }
  }, [
    category,
    fetchFeeds,
    isFirstPageFetched,
    isFirstPageFetching,
    isFromShortsEntry,
    itemIds.length,
  ]);

  // Fetch clip feeds first page data when component mount
  useEffect(() => {
    // Only need clip feeds data on shorts entry
    if (!isFromShortsEntry) return;

    const isFeedIdChanged = !!(
      clipLatestFeedId && clipLatestFeedId != clipFetchedFeedId
    );
    const canFetch = !isClipFirstPageFetched && !isClipFirstPageFetching;

    if (isConfigMerged && (isFeedIdChanged || canFetch) && !!clipCategory) {
      log('Fetch clip feeds first page data when component mount', {
        clipCategory,
      });
      fetchFeeds({
        page: 1,
        type: clipCategory,
        id: clipLatestFeedId,
        unixTimestamp: clipLatestTimestamp ? clipLatestTimestamp : undefined,
        shouldSkipLocalCache: !!clipLatestTimestamp,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch shorts category feeds first page data and prepend `idFromUrl` if exist
  useEffect(() => {
    if (!isFromShortsEntry || !isConfigMerged || !category) return;

    const idFromUrl = messageIdFromUrl || ctaIdFromUrl;

    if (!itemIds.length) {
      const mongoObjectIdRegex = new RegExp(mongoObjectIdRegexPattern, 'i');
      const isAd = !mongoObjectIdRegex.test(idFromUrl);
      if (idFromUrl) {
        if (isAd) {
          addBreadcrumb({
            category: 'ShortAd',
            level: Severity.Debug,
            message: 'set first id',
            data: {
              idFromUrl,
              itemIds,
            },
          });
        }

        log('Set `currentIndex` to `0`', {
          firstId: idFromUrl,
        });
        setCurrentIndex(0);
      }

      log('Fetch shorts category feeds first page data', {
        category,
      });

      fetchFeeds({
        type: category,
        isAllowSameId: true,
        shouldReplaceIds: true,
        shouldIgnorePage: true,
        shouldSkipLocalCache: true,
        selectPath: ['shorts', category],
        unixTimestamp: getCurrentUnixTimestamp(), // force fetch new; make feed as random as possible
        prependIds: [isAd ? `cta-${ctaIdFromUrl}` : messageIdFromUrl], // to replace original setFirstId
      });
    }
  }, [
    isConfigMerged,
    messageIdFromUrl,
    category,
    itemIds,
    ctaIdFromUrl,
    fetchFeeds,
    mongoObjectIdRegexPattern,
    isFromShortsEntry,
  ]);

  // Replace url with next messageId if currentIndex changed.
  useEffect(() => {
    const id = itemIds[currentIndex];
    const isAd = id?.startsWith('cta-');
    const shouldReplaceToShortsDetail =
      id && (isAd ? id !== 'cta-' + ctaIdFromUrl : id !== messageIdFromUrl);
    if (shouldReplaceToShortsDetail) {
      // No need to append `window.location.search`
      // since navigationAction would take care of it.
      const replacedUrl = `/shorts/${isAd ? `ad/${id.replace('cta-', '')}` : id}`;
      if (isAd) {
        addBreadcrumb({
          category: 'ShortAd',
          level: Severity.Debug,
          message: 'replace url /shorts to /shorts/cta/' + ctaIdFromUrl,
          data: {
            id,
            ctaIdFromUrl,
            replacedUrl,
            itemIds,
          },
        });
      }

      log('Replace shorts url', {
        id,
        ids: itemIds,
        category,
      });

      replace(replacedUrl, {
        listPath,
        category,
      });
    }
  }, [
    messageIdFromUrl,
    ctaIdFromUrl,
    currentIndex,
    itemIds,
    listPath,
    category,
    replace,
  ]);

  useEffect(() => {
    const messageId = itemIds[currentIndex];
    if (messageId) {
      addMessageIdToShortWatchedList({ messageId });
    }
  }, [currentIndex, itemIds, addMessageIdToShortWatchedList]);

  // Pre-fetch shorts next page data
  useEffect(() => {
    const shouldFetchNextPage =
      itemIds.length > 0 &&
      itemIds.length - PREFETCH_OFFSET <= currentIndex &&
      !isNextPageFetching;
    if (shouldFetchNextPage) {
      if (nextPage != null) {
        fetchFeeds({
          page: nextPage,
          type: category,
          isAllowSameId: isFromShortsEntry,
          selectPath: shortsSelectPath,
        });
      }
      if (clipNextPage != null) {
        fetchFeeds({
          page: clipNextPage,
          type: clipCategory,
        });
      }
    }
  }, [
    category,
    clipCategory,
    clipNextPage,
    fetchFeeds,
    isNextPageFetching,
    itemIds.length,
    currentIndex,
    nextPage,
    shortsSelectPath,
    isFromShortsEntry,
  ]);

  useEffect(() => {
    if (deviceHeight && swiperRef?.current) {
      swiperRef.current?.update();
    }
  }, [deviceHeight]);

  useEffect(() => {
    return () => {
      setFirstShortMessagePlayed({ hasPlayed: false });
    };
  }, [setFirstShortMessagePlayed]);

  const handleSlidePrev = useCallback(() => {
    swiperRef.current?.slidePrev();
  }, []);
  const handleSlideNext = useCallback(() => {
    swiperRef.current?.slideNext();
  }, []);

  if (!isServer && !messageIdFromUrl && !category && isConfigMerged) {
    return <Redirect to="/" status={302} />;
  }

  if (!itemIds?.length || !deviceHeight) {
    return (
      <StyledShortsDetail
        deviceHeight={deviceHeight}
        isIphoneXSeriesPWA={isIphoneXSeriesPWA}
      >
        <ShortMessage
          messageId={messageIdFromUrl}
          shouldShowBack={shouldShowBack}
          deviceHeight={deviceHeight}
          isActive
        />
      </StyledShortsDetail>
    );
  }

  const hasHeightEnough =
    deviceHeight && deviceHeight > MIN_DESKTOP_LAYOUT_HEIGHT;
  const shouldDisplaySidebar = isConfigMerged && itemIds?.length > 1;

  return (
    <StyledShortsDetail
      deviceHeight={deviceHeight}
      isIphoneXSeriesPWA={isIphoneXSeriesPWA}
    >
      <Container>
        <StyledSwiper
          modules={['Virtual', 'Keyboard', 'Mousewheel']}
          direction={'vertical'}
          initialSlide={currentIndex}
          onSwiper={swiper => {
            window.swiperRef = swiper;
            swiperRef.current = swiper;
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              return swiper.update();
            }, 0);
          }}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          simulateTouch={!isOnDesktop}
          touchAngle={45}
          threshold={20}
          virtual={{
            addSlidesBefore: VIRTUAL_OFFSET,
            addSlidesAfter: VIRTUAL_OFFSET,
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          mousewheel={{
            enabled: isOnDesktop,
            // invert: true,
            // sensitivity: 1,
            thresholdDelta: 90,
            // thresholdTime: 500,
          }}
          touchStartPreventDefault={false}
          allowTouchMove={true}
          onTouchStart={(swiper, event) => {
            if (event.touches && event.touches[0]) {
              swiper.touchStartX = event.touches[0].clientX;
              swiper.touchStartY = event.touches[0].clientY;
            }
          }}
          onTouchMove={(swiper, event) => {
            if (
              event.touches &&
              event.touches[0] &&
              swiper.touchStartX &&
              swiper.touchStartY
            ) {
              const deltaX = Math.abs(
                event.touches[0].clientX - swiper.touchStartX
              );
              const deltaY = Math.abs(
                event.touches[0].clientY - swiper.touchStartY
              );

              if (deltaX > deltaY && deltaX > 20) {
                swiper.allowTouchMove = false;
                return false;
              } else if (deltaY > 20) {
                swiper.allowTouchMove = true;
              }
            }
          }}
          onTouchEnd={swiper => {
            swiper.allowTouchMove = true;
            swiper.touchStartX = null;
            swiper.touchStartY = null;
          }}
          onTransitionEnd={({ activeIndex: currentIndex }) => {
            // (2024.06)
            // Only keep the active index for the Shorts started from header/footer
            if (feedId && isFromShortsEntry) {
              log(
                'Set `activeIndex` into `lastWatchedIndex` when transition end',
                {
                  feedId,
                  activeIndex: currentIndex,
                }
              );
              return setLastWatchedIndex({
                lastWatchedIndex: currentIndex,
                feedId,
              });
            }
          }}
          onSlideChange={({ activeIndex }) => {
            // Can't use `lastWatchedIndex` as counter, since `lastWatchedIndex` trigger onTransitionEnd,
            // it might miss the middle ids if index change very fast by tap or press hotkey
            log('Set `activeIndex` into `currentIndex` when slide change', {
              activeIndex,
            });
            setCurrentIndex(activeIndex);
          }}
          breakpoints={{
            768: {
              slidesPerView: hasHeightEnough ? 1.1 : 1,
              centeredSlides: true,
              spaceBetween: 36,
            },
          }}
        >
          {itemIds.map((id, index) => (
            <SwiperSlide key={`${id}-${index}`}>
              {id.startsWith('cta-') ? (
                <ShortAd
                  adId={id}
                  key={`${id}-${index}`}
                  isActive={index === currentIndex}
                />
              ) : (
                <ShortMessage
                  key={`${id}-${index}`}
                  messageId={id}
                  isActive={index === currentIndex}
                  shouldShowBack={shouldShowBack}
                  deviceHeight={deviceHeight}
                  shouldPrefetch={
                    // If there is any Short that has been activated (currentIndex >= 0),
                    // prefetch the segments for activated one and the next two (if any)
                    currentIndex >= 0 &&
                    index >= currentIndex &&
                    index <= currentIndex + prefetchedMessageAmount
                  }
                />
              )}
            </SwiperSlide>
          ))}
        </StyledSwiper>

        {shouldDisplaySidebar && (
          <ShortSidebar
            isOnFirst={currentIndex === 0}
            isOnLast={currentIndex === itemIds.length - 1}
            slideNext={handleSlideNext}
            slidePrev={handleSlidePrev}
          />
        )}
      </Container>
    </StyledShortsDetail>
  );
};

ShortsDetail.propTypes = {
  messageIdFromUrl: PropTypes.string,
  ctaIdFromUrl: PropTypes.string,
  feedId: PropTypes.string,
  clipFetchedFeedId: PropTypes.string,
  clipLatestFeedId: PropTypes.string,
  category: PropTypes.string,
  clipCategory: PropTypes.string,
  deviceHeight: PropTypes.number,
  isOnDesktop: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  isClipFirstPageFetching: PropTypes.bool,
  isClipFirstPageFetched: PropTypes.bool,
  isConfigMerged: PropTypes.bool,
  prefetchedMessageAmount: PropTypes.number,
  setLastWatchedIndex: PropTypes.func,
  replace: PropTypes.func,
  fetchFeeds: PropTypes.func,
  itemIds: PropTypes.array,
  shortsSelectPath: PropTypes.array,
  nextPage: PropTypes.number,
  clipNextPage: PropTypes.number,
  clipLatestTimestamp: PropTypes.number,
  mongoObjectIdRegexPattern: PropTypes.string,
  setFirstShortMessagePlayed: PropTypes.func,
  addMessageIdToShortWatchedList: PropTypes.func,
};

const StyledShortsDetail = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
  height: ${({ deviceHeight }) =>
    deviceHeight ? `${deviceHeight}px` : '100vh'};
  padding-top: calc(var(--space-to-pinned-notification) + ${headerHeight}px);
  ${media.mobile`
    padding-top: var(--space-to-pinned-notification);
    padding-bottom: 0;
    height: ${({ deviceHeight, isIphoneXSeriesPWA }) =>
      isIphoneXSeriesPWA
        ? `${
            deviceHeight -
            footerNavigationHeight -
            iPhoneXSeriesFooterPaddingInPWA
          }px`
        : `${deviceHeight - footerNavigationHeight}px`};
  `};
`;

const StyledSwiper = styled(Swiper)`
  .swiper-wrapper {
    display: flex;
    flex-direction: column;
  }
`;

const Container = styled.div`
  position: relative;
  display: inline-block;
  height: 100%;
  width: auto;
  ${media.mobile`
    width: 100%;
  `}
`;

export default ShortsDetail;
